'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import { InterfaceChainUtils } from '@morpho-org/dapp-core';
import { MarketCell, VaultCell } from '@morpho-org/ui-v6/client';
import { CellSimpleText } from '@morpho-org/ui-v6/server';
import { getMarketName } from '@morpho-org/ui-v6/utils';
import { Badge, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { abbreviateAddress } from '@repo/utils';
import { ButtonLink } from '@/components/common/ButtonLink';
import { InternalLink } from '@/components/common/InternalLink';
import type { AllocationTarget } from '../config/VaultV2AllocationTransactions.types';
export interface AllocationTargetCellProps {
  target: AllocationTarget;
  chainId: ChainId;
  /** Vault underlying-asset logo, reused for vault targets. */
  logoURI?: string | null;
}

/**
 * Renders the "Vault / Market" column: the vault or market(s) an allocation
 * event moved assets to (Allocate) or from (Deallocate).
 */
const AllocationTargetCell = t0 => {
  const $ = _c(42);
  const {
    target,
    chainId,
    logoURI
  } = t0;
  if (target.kind === "vault") {
    let t1;
    if ($[0] !== target.address || $[1] !== target.name) {
      t1 = target.name ?? abbreviateAddress(target.address);
      $[0] = target.address;
      $[1] = target.name;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] !== chainId || $[4] !== logoURI || $[5] !== t1 || $[6] !== target.address || $[7] !== target.isVaultV2) {
      t2 = <VaultCell nameClassName="max-w-[240px]" address={target.address} name={t1} logoURI={logoURI} chainId={chainId} isVaultV2={target.isVaultV2} />;
      $[3] = chainId;
      $[4] = logoURI;
      $[5] = t1;
      $[6] = target.address;
      $[7] = target.isVaultV2;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    const cell = t2;
    if (target.url) {
      let t3;
      if ($[9] !== cell || $[10] !== target.url) {
        t3 = <InternalLink href={target.url} withHoverEffect={true}>{cell}</InternalLink>;
        $[9] = cell;
        $[10] = target.url;
        $[11] = t3;
      } else {
        t3 = $[11];
      }
      return t3;
    }
    let t3;
    if ($[12] !== cell) {
      t3 = <div className="pl-[7px]">{cell}</div>;
      $[12] = cell;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    return t3;
  }
  if (target.kind === "markets") {
    let firstMarket;
    let firstUnresolvedId;
    let t1;
    if ($[14] !== target.markets || $[15] !== target.unresolvedMarketIds) {
      const [t2, ...otherMarkets] = target.markets;
      firstMarket = t2;
      const [t3, ...otherUnresolvedIds] = target.unresolvedMarketIds;
      firstUnresolvedId = t3;
      t1 = [...otherMarkets.map(_temp), ...(firstMarket ? target.unresolvedMarketIds : otherUnresolvedIds).map(_temp2)];
      $[14] = target.markets;
      $[15] = target.unresolvedMarketIds;
      $[16] = firstMarket;
      $[17] = firstUnresolvedId;
      $[18] = t1;
    } else {
      firstMarket = $[16];
      firstUnresolvedId = $[17];
      t1 = $[18];
    }
    const hiddenMarketLabels = t1;
    let t2;
    if ($[19] !== chainId || $[20] !== firstMarket || $[21] !== firstUnresolvedId) {
      t2 = firstMarket ? <MarketCell collateralAsset={firstMarket.collateralAsset} loanAsset={firstMarket.loanAsset} lltv={firstMarket.lltv} chainId={chainId} /> : firstUnresolvedId ? <CellSimpleText>{abbreviateAddress(firstUnresolvedId)}</CellSimpleText> : null;
      $[19] = chainId;
      $[20] = firstMarket;
      $[21] = firstUnresolvedId;
      $[22] = t2;
    } else {
      t2 = $[22];
    }
    const primaryCell = t2;
    if (!primaryCell) {
      return null;
    }
    let t3;
    if ($[23] !== chainId || $[24] !== firstMarket || $[25] !== firstUnresolvedId) {
      t3 = firstMarket ? firstMarket.url : firstUnresolvedId ? `/${InterfaceChainUtils.getIdentifierById(chainId)}/variable/${firstUnresolvedId}` : undefined;
      $[23] = chainId;
      $[24] = firstMarket;
      $[25] = firstUnresolvedId;
      $[26] = t3;
    } else {
      t3 = $[26];
    }
    const primaryUrl = t3;
    let t4;
    if ($[27] !== primaryCell || $[28] !== primaryUrl) {
      t4 = primaryUrl ? <InternalLink href={primaryUrl} withHoverEffect={true}>{primaryCell}</InternalLink> : <div className="pl-[7px]">{primaryCell}</div>;
      $[27] = primaryCell;
      $[28] = primaryUrl;
      $[29] = t4;
    } else {
      t4 = $[29];
    }
    let t5;
    if ($[30] !== hiddenMarketLabels) {
      t5 = hiddenMarketLabels.length > 0 && <Tooltip><TooltipTrigger asChild={true}><Badge variant="tertiary" size="sm" tabIndex={0}>+{hiddenMarketLabels.length}</Badge></TooltipTrigger><TooltipContent>{hiddenMarketLabels.join(", ")}</TooltipContent></Tooltip>;
      $[30] = hiddenMarketLabels;
      $[31] = t5;
    } else {
      t5 = $[31];
    }
    let t6;
    if ($[32] !== t4 || $[33] !== t5) {
      t6 = <div className="gap-xs flex items-center">{t4}{t5}</div>;
      $[32] = t4;
      $[33] = t5;
      $[34] = t6;
    } else {
      t6 = $[34];
    }
    return t6;
  }
  const t1 = target.url;
  let t2;
  if ($[35] !== target.address) {
    t2 = abbreviateAddress(target.address);
    $[35] = target.address;
    $[36] = t2;
  } else {
    t2 = $[36];
  }
  let t3;
  if ($[37] !== t2) {
    t3 = {
      variant: "ghost",
      size: "default",
      label: t2
    };
    $[37] = t2;
    $[38] = t3;
  } else {
    t3 = $[38];
  }
  let t4;
  if ($[39] !== t3 || $[40] !== target.url) {
    t4 = <ButtonLink href={t1} mode="external" buttonProps={t3} />;
    $[39] = t3;
    $[40] = target.url;
    $[41] = t4;
  } else {
    t4 = $[41];
  }
  return t4;
};
export default AllocationTargetCell;
function _temp(market) {
  return `${getMarketName({
    loanAsset: market.loanAsset,
    collateralAsset: market.collateralAsset
  })} (${abbreviateAddress(market.marketId)})`;
}
function _temp2(marketId) {
  return abbreviateAddress(marketId);
}